import * as React from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { Plugin } from 'bundles/coursera-toolbar/types/Plugin';
import { applyPluginOrder } from 'bundles/coursera-toolbar/utils/pluginOrder';
import type { ToolbarSettings } from 'bundles/coursera-toolbar/utils/settings';
import { getDefaultSettings, loadSettings, saveSettings } from 'bundles/coursera-toolbar/utils/settings';

export type ToolbarSettingsContextValue = {
  isMinimized: boolean;
  toggleMinimized: () => void;
  position: { x: number | null; y: number | null };
  updatePosition: (newPosition: { x: number; y: number }) => void;
  resetPosition: () => void;
  bottomLocked: boolean;
  pluginEnabledRecord: Record<string, boolean>;
  isPluginEnabled: (plugin: Plugin) => boolean;
  setPluginEnabled: (pluginId: string, enabled: boolean) => void;
  /** Sort plugins by saved toolbar order (and registry default when unset). */
  orderPlugins: (plugins: Plugin[]) => Plugin[];
  /** Persist new full plugin id order (e.g. after drag-and-drop). */
  setPluginOrder: (pluginIds: string[]) => void;
};

const ToolbarSettingsContext = createContext<ToolbarSettingsContextValue | null>(null);

function constrainPositionToViewport(
  pos: { x: number; y: number },
  willBeMinimized: boolean
): { x: number; y: number } {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const toolbarWidth = willBeMinimized ? 80 : 400;
  const toolbarHeight = willBeMinimized ? 28 : 50;
  const constrainedX = Math.max(0, Math.min(pos.x, viewportWidth - toolbarWidth));
  const constrainedY = Math.max(0, Math.min(pos.y, viewportHeight - toolbarHeight));
  return { x: constrainedX, y: constrainedY };
}

type ToolbarSettingsProviderProps = {
  children: ReactNode;
};

export function ToolbarSettingsProvider({ children }: ToolbarSettingsProviderProps) {
  const [settings, setSettings] = useState<ToolbarSettings>(() =>
    typeof window !== 'undefined' ? loadSettings() : getDefaultSettings()
  );

  const updateSettings = useCallback<(updater: (prev: ToolbarSettings) => ToolbarSettings) => void>((updater) => {
    setSettings((prev) => {
      const next = updater(prev);
      saveSettings(next);
      return next;
    });
  }, []);

  const toggleMinimized = useCallback<() => void>(() => {
    updateSettings((prev) => {
      const newState = !prev.isMinimized;
      let next: ToolbarSettings = { ...prev, isMinimized: newState };
      if (prev.position.x !== null && prev.position.y !== null) {
        const constrained = constrainPositionToViewport({ x: prev.position.x, y: prev.position.y }, newState);
        if (constrained.x !== prev.position.x || constrained.y !== prev.position.y) {
          next = { ...next, position: constrained };
        }
      }
      return next;
    });
  }, [updateSettings]);

  const updatePosition = useCallback<(newPosition: { x: number; y: number }) => void>(
    (newPosition) => {
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
      const toolbarHeight = settings.isMinimized ? 28 : 36;
      const distanceFromBottom = viewportHeight - newPosition.y - toolbarHeight;
      const shouldBottomLock = distanceFromBottom <= 60;
      const storedPosition = {
        x: newPosition.x,
        y: shouldBottomLock ? distanceFromBottom : newPosition.y,
      };
      updateSettings((prev) => ({
        ...prev,
        position: storedPosition,
        bottomLocked: shouldBottomLock,
      }));
    },
    [settings.isMinimized, updateSettings]
  );

  const resetPosition = useCallback<() => void>(() => {
    updateSettings((prev) => ({
      ...prev,
      position: { x: null, y: null },
      bottomLocked: false,
    }));
  }, [updateSettings]);

  const isPluginEnabled = useCallback<(plugin: Plugin) => boolean>(
    (plugin) => {
      const stored = settings.pluginEnabled[plugin.id];
      if (stored !== undefined) return stored;
      return plugin.defaultEnabled !== false;
    },
    [settings.pluginEnabled]
  );

  const setPluginEnabled = useCallback<(pluginId: string, enabled: boolean) => void>(
    (pluginId, enabled) => {
      updateSettings((prev) => ({
        ...prev,
        pluginEnabled: { ...prev.pluginEnabled, [pluginId]: enabled },
      }));
    },
    [updateSettings]
  );

  const orderPlugins = useCallback<(plugins: Plugin[]) => Plugin[]>(
    (plugins) => applyPluginOrder(plugins, settings.pluginOrder),
    [settings.pluginOrder]
  );

  const setPluginOrder = useCallback<(pluginIds: string[]) => void>(
    (pluginIds) => {
      updateSettings((prev) => ({ ...prev, pluginOrder: pluginIds }));
    },
    [updateSettings]
  );

  const value = useMemo<ToolbarSettingsContextValue>(
    () => ({
      isMinimized: settings.isMinimized,
      toggleMinimized,
      position: settings.position,
      updatePosition,
      resetPosition,
      bottomLocked: settings.bottomLocked,
      pluginEnabledRecord: settings.pluginEnabled,
      isPluginEnabled,
      setPluginEnabled,
      orderPlugins,
      setPluginOrder,
    }),
    [
      settings.isMinimized,
      settings.position,
      settings.bottomLocked,
      settings.pluginEnabled,
      toggleMinimized,
      updatePosition,
      resetPosition,
      isPluginEnabled,
      setPluginEnabled,
      orderPlugins,
      setPluginOrder,
    ]
  );

  return <ToolbarSettingsContext.Provider value={value}>{children}</ToolbarSettingsContext.Provider>;
}

export { ToolbarSettingsContext };
